import React, { useState } from 'react';
import { FaUserTie, FaCalculator, FaHistory, FaPercent, FaPlus, FaEdit, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import './Salary.css';

const Salary = () => {
  const [activeTab, setActiveTab] = useState('add');
  const [salaryPercentage, setSalaryPercentage] = useState('10');
  const [selectedUser, setSelectedUser] = useState('');
  const [salaryAmount, setSalaryAmount] = useState('');
  const [salaryDate, setSalaryDate] = useState('');
  const [autoCalculation, setAutoCalculation] = useState(false);

  // Dummy salary history
  const [salaryHistory] = useState([
    {
      id: 1,
      user: 'John Doe',
      email: 'john@example.com',
      amount: '$1,500',
      percentage: '10%',
      baseAmount: '$15,000',
      date: '2024-01-15',
      type: 'Auto',
      status: 'Paid'
    },
    {
      id: 2,
      user: 'Jane Smith',
      email: 'jane@example.com',
      amount: '$2,000',
      percentage: '12%',
      baseAmount: '$16,667',
      date: '2024-01-15',
      type: 'Manual',
      status: 'Paid'
    },
    {
      id: 3,
      user: 'Mike Johnson',
      email: 'mike@example.com',
      amount: '$800',
      percentage: '8%',
      baseAmount: '$10,000',
      date: '2024-01-14',
      type: 'Auto',
      status: 'Pending'
    },
    {
      id: 4,
      user: 'Sarah Williams',
      email: 'sarah@example.com',
      amount: '$1,200',
      percentage: '10%',
      baseAmount: '$12,000',
      date: '2024-01-14',
      type: 'Auto',
      status: 'Paid'
    },
    {
      id: 5,
      user: 'Robert Brown',
      email: 'robert@example.com',
      amount: '$3,000',
      percentage: '15%',
      baseAmount: '$20,000',
      date: '2024-01-13',
      type: 'Manual',
      status: 'Paid'
    }
  ]);

  // Dummy users for salary selection
  const users = [
    { id: 1, name: 'John Doe', email: 'john@example.com', balance: '$15,000' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', balance: '$16,667' },
    { id: 3, name: 'Mike Johnson', email: 'mike@example.com', balance: '$10,000' },
    { id: 4, name: 'Sarah Williams', email: 'sarah@example.com', balance: '$12,000' },
    { id: 5, name: 'Robert Brown', email: 'robert@example.com', balance: '$20,000' }
  ];

  const handleAddSalary = (e) => {
    e.preventDefault();
    console.log('Adding salary:', { selectedUser, salaryAmount, salaryDate });
    // Implement add salary logic
  };

  const handleUpdatePercentage = () => {
    console.log('Updating salary percentage:', salaryPercentage);
    // Implement percentage update logic
  };

  const handleAutoCalculation = () => {
    console.log('Running auto salary calculation');
    // Implement auto calculation logic
  };

  return (
    <div className="salary-page">
      <div className="page-header">
        <h1>Salary Management</h1>
        <p>Manage salary distribution and settings</p>
      </div>

      {/* Stats Cards */}
      <div className="salary-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <FaUserTie />
          </div>
          <div className="stat-info">
            <h4>Total Salary Paid</h4>
            <p>$89,450</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info">
            <FaCalculator />
          </div>
          <div className="stat-info">
            <h4>This Month</h4>
            <p>$12,500</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <FaPercent />
          </div>
          <div className="stat-info">
            <h4>Average Percentage</h4>
            <p>10.5%</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FaHistory />
          </div>
          <div className="stat-info">
            <h4>Pending Salaries</h4>
            <p>3</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="salary-tabs">
        <button
          className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          <FaPlus /> Add Salary
        </button>
        <button
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <FaEdit /> Salary Settings
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FaHistory /> Salary History
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Add Salary Tab */}
        {activeTab === 'add' && (
          <div className="salary-form-container">
            <h3>Add Salary Manually</h3>
            <form onSubmit={handleAddSalary} className="salary-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Select User</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Choose a user...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} - {user.email} (Balance: {user.balance})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Salary Amount</label>
                  <input
                    type="text"
                    value={salaryAmount}
                    onChange={(e) => setSalaryAmount(e.target.value)}
                    className="form-control"
                    placeholder="Enter amount..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Salary Date</label>
                  <input
                    type="date"
                    value={salaryDate}
                    onChange={(e) => setSalaryDate(e.target.value)}
                    className="form-control"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Add any notes about this salary payment..."
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary">
                Add Salary
              </button>
            </form>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="salary-settings-container">
            <h3>Salary Configuration</h3>

            <div className="settings-section">
              <h4>Salary Percentage Settings</h4>
              <div className="percentage-control">
                <label>Default Salary Percentage</label>
                <div className="percentage-input-group">
                  <input
                    type="number"
                    value={salaryPercentage}
                    onChange={(e) => setSalaryPercentage(e.target.value)}
                    className="form-control"
                    min="0"
                    max="100"
                  />
                  <span className="percentage-symbol">%</span>
                  <button
                    className="btn btn-primary"
                    onClick={handleUpdatePercentage}
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h4>Auto Salary Calculation</h4>
              <div className="auto-calc-control">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={autoCalculation}
                    onChange={(e) => setAutoCalculation(e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
                <span>Enable automatic salary calculation</span>
              </div>
              {autoCalculation && (
                <div className="auto-calc-info">
                  <p>Salaries will be automatically calculated and distributed on the 1st of each month.</p>
                  <button
                    className="btn btn-success"
                    onClick={handleAutoCalculation}
                  >
                    <FaCalculator /> Run Calculation Now
                  </button>
                </div>
              )}
            </div>

            <div className="settings-section">
              <h4>User-wise Salary Percentages</h4>
              <div className="user-percentages">
                {users.map(user => (
                  <div key={user.id} className="user-percentage-row">
                    <span className="user-name">{user.name}</span>
                    <input
                      type="number"
                      className="form-control small"
                      defaultValue="10"
                      min="0"
                      max="100"
                    />
                    <span className="percentage-symbol">%</span>
                  </div>
                ))}
              </div>
              <button className="btn btn-primary">Save User Percentages</button>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="salary-history-container">
            <div className="history-header">
              <h3>Salary History</h3>
              <div className="history-actions">
                <input
                  type="month"
                  className="form-control"
                  defaultValue="2024-01"
                />
                <button className="btn btn-export">
                  <FaDownload /> Export Report
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="salary-history-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Percentage</th>
                    <th>Base Amount</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryHistory.map(record => (
                    <tr key={record.id}>
                      <td>#{record.id}</td>
                      <td>
                        <div className="user-info">
                          <span className="user-name">{record.user}</span>
                          <small className="user-email">{record.email}</small>
                        </div>
                      </td>
                      <td className="amount">{record.amount}</td>
                      <td>{record.percentage}</td>
                      <td>{record.baseAmount}</td>
                      <td>
                        <span className={`type-badge type-${record.type.toLowerCase()}`}>
                          {record.type}
                        </span>
                      </td>
                      <td>{record.date}</td>
                      <td>
                        <span className={`status-badge status-${record.status.toLowerCase()}`}>
                          {record.status}
                        </span>
                      </td>
                      <td>
                        <button className="action-btn view">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Salary;